import type { OS } from '../hooks/useOS';

/**
 * Safely parses a JSON string and returns the parsed object or null if parsing fails
 * @param jsonString - The JSON string to parse
 * @param fallback - Optional fallback value to return if parsing fails (defaults to null)
 * @returns The parsed object or the fallback value
 */
export const safeParseJson = <T = unknown>(
  jsonString: string | null | undefined,
  fallback: T | null = null,
): T | null => {
  if (!jsonString) {
    return fallback;
  }

  try {
    return JSON.parse(jsonString) as T;
  } catch (error) {
    console.warn('Failed to parse JSON:', error);
    return fallback;
  }
};

const keyMap: Record<string, { mac: string; others: string }> = {
  shift: { mac: '⇧', others: '⇧' },
  alt: { mac: '⌥', others: 'Alt' },
  ctrl: { mac: 'Ctrl', others: 'Ctrl' },
  meta: { mac: '⌘', others: '⊞' },
  mod: { mac: '⌘', others: 'Ctrl' },
  backspace: { mac: '⌫', others: '⌫' },
  tab: { mac: '⇥', others: '⇥' },
  enter: { mac: '↩', others: '↩' },
  return: { mac: '↩', others: '↩' },
  esc: { mac: 'Esc', others: 'Esc' },
  escape: { mac: 'Esc', others: 'Esc' },
  space: { mac: '␣', others: '␣' },
  up: { mac: '↑', others: '↑' },
  down: { mac: '↓', others: '↓' },
  left: { mac: '←', others: '←' },
  right: { mac: '→', others: '→' },
  pageup: { mac: 'PageUp', others: 'PageUp' },
  pagedown: { mac: 'PageDown', others: 'PageDown' },
  delete: { mac: '⌦', others: '⌦' },
  del: { mac: '⌦', others: '⌦' },
};

export function hotkeyToSymbol(hotkey: string, platform: OS = 'mac'): string {
  const keys = hotkey.toLowerCase().split('+');

  return keys
    .map(key => {
      const mapped = keyMap[key.trim()];
      return mapped
        ? mapped[platform === 'mac' ? 'mac' : 'others']
        : key.toUpperCase();
    })
    .join('');
}

/**
 * Truncates a string to a maximum length and adds ellipsis if truncated
 * @param str - The string to truncate
 * @param maxLength - Maximum length before truncation (null/undefined means no truncation)
 * @param ellipsis - The string to append when truncated (defaults to '...')
 * @returns The original string if under max length, or truncated string with ellipsis
 */
export function truncateString(
  str: string,
  maxLength: number | null | undefined,
  ellipsis = '...',
): string {
  // No truncation if maxLength is not provided or string is within limit
  if (maxLength == null || str.length <= maxLength) {
    return str;
  }

  // Truncate and add ellipsis
  return str.substring(0, maxLength) + ellipsis;
}

/**
 * Shortens a filename from the middle, keeping the start, the tail and the
 * extension.
 *
 * Prefer this over {@link truncateString} for filenames: sibling files often
 * share a long prefix, so cutting the end collapses them into the same label
 * (`SDS-Billing By Item Co13 Part1 1st to 15th.txt` and `…Part2 16th to
 * End.txt` both become `SDS-Billing By Item Co13 Part…`).
 *
 * @param filename - The full filename
 * @param maxLength - Maximum length of the returned string
 * @returns The filename, shortened with an ellipsis in the middle if needed
 * @example
 * truncateFilename('SDS-Billing By Item Co13 Part1 1st to 15th.txt', 28)
 * // 'SDS-Billing …1st to 15th.txt'
 */
export function truncateFilename(filename: string, maxLength: number): string {
  if (!filename || filename.length <= maxLength) return filename;

  const dotIndex = filename.lastIndexOf('.');
  // A leading dot ('.env') is part of the stem, not an extension.
  const hasExtension = dotIndex > 0;
  const extension = hasExtension ? filename.slice(dotIndex) : '';
  const stem = hasExtension ? filename.slice(0, dotIndex) : filename;

  const budget = maxLength - extension.length - 1;
  // Too little room to say anything on both sides — cut the end instead.
  if (budget < 8) return `${filename.slice(0, Math.max(0, maxLength - 1))}…`;

  // Weight the tail: the distinguishing token is usually nearer the end.
  const tailLength = Math.ceil(budget * 0.45);
  const headLength = budget - tailLength;

  return `${stem.slice(0, headLength)}…${stem.slice(stem.length - tailLength)}${extension}`;
}

/**
 * Gets the first letter of a string capitalized
 * @param str - The string to get the initial from
 * @returns The first letter capitalized, or empty string if str is empty/undefined
 */
export function getInitial(str: string | undefined | null): string {
  const trimmedStr = str?.trim();
  if (!trimmedStr) {
    return '';
  }
  return trimmedStr.charAt(0).toUpperCase();
}

/**
 * Converts a value to a string for input display.
 * Handles null/undefined values by returning an empty string.
 *
 * Useful for form input values where null/undefined should display as empty.
 *
 * @param value - The value to convert
 * @returns The string representation of the value, or empty string if null/undefined
 * @example
 * toInputValue('hello') // 'hello'
 * toInputValue(123) // '123'
 * toInputValue(null) // ''
 * toInputValue(undefined) // ''
 */
export function toInputValue(value: unknown): string {
  return value != null ? String(value) : '';
}

/**
 * Formats a display name by replacing underscores with spaces and capitalizing the first letter.
 * Useful for converting snake_case identifiers to human-readable labels.
 *
 * @param name - The name to format (e.g., 'prime_sum')
 * @returns The formatted name with spaces and first letter capitalized (e.g., 'Prime sum')
 * @example
 * formatDisplayName('prime_sum') // 'Prime sum'
 * formatDisplayName('user_name') // 'User name'
 * formatDisplayName('hello') // 'Hello'
 * formatDisplayName('') // ''
 */
export function formatDisplayName(name: string): string {
  if (!name) {
    return '';
  }

  const formatted = name.split('_').join(' ');
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

/**
 * Strips a leading numbered list prefix from a string.
 * Matches patterns like "1. ", "12. ", "123." at the start of the string.
 *
 * @param str - The string to process
 * @returns The string with leading number prefix removed and trimmed
 * @example
 * stripLeadingNumber('1. Read input values') // 'Read input values'
 * stripLeadingNumber('12. Process data') // 'Process data'
 * stripLeadingNumber('No number here') // 'No number here'
 */
export function stripLeadingNumber(str: string): string {
  return str.replace(/^\d+\.\s*/, '').trim();
}

/**
 * Renders an unknown value as display text: strings pass through, other
 * primitives stringify, and objects/arrays fall back to JSON.
 *
 * @param value - The value to render
 * @param nullish - Placeholder for null/undefined, and for values JSON can't
 * represent (functions, symbols). Defaults to an empty string
 * @returns A string safe to render
 * @example
 * toDisplayString('open') // 'open'
 * toDisplayString(180) // '180'
 * toDisplayString([1, 2]) // '[1,2]'
 * toDisplayString(null, '—') // '—'
 */
export function toDisplayString(value: unknown, nullish = ''): string {
  if (value == null) return nullish;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value) ?? nullish;
  } catch {
    // Circular structures and BigInt throw; the raw coercion still says something.
    return String(value);
  }
}
